import { Link, useRouter } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import { useLogoutMutation } from "@/api/auth";
import { useTeamsQuery, useSetActiveTeamMutation } from "@/api/teams";
import { useState, useRef, useEffect } from "react";
import {
  applyThemePreference,
  getThemePreference,
  type ThemePreference,
} from "@/lib/theme";

const THEME_LABELS: Record<ThemePreference, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

export function Header() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeTeamId = useAuthStore((s) => s.activeTeamId);
  const setActiveTeam = useAuthStore((s) => s.setActiveTeam);
  const logoutMutation = useLogoutMutation();
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTeams, setShowTeams] = useState(false);
  const [theme, setTheme] = useState<ThemePreference>(() => getThemePreference());
  const menuRef = useRef<HTMLDivElement>(null);

  const { data: teams } = useTeamsQuery();
  const setActiveMutation = useSetActiveTeamMutation();

  // Sync teams to store
  useEffect(() => {
    if (teams) useAuthStore.getState().setTeams(teams);
  }, [teams]);

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setShowTeams(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const activeTeam = teams?.find((t) => t.id === activeTeamId);
  const teamName = activeTeam?.name ?? user?.username ?? "Personal";

  function handleLogout() {
    logoutMutation.mutate(undefined, {
      onSuccess: () => void router.navigate({ to: "/login" }),
    });
  }

  function handleSwitchTeam(teamId: string) {
    setActiveTeam(teamId);
    setActiveMutation.mutate(teamId);
    setShowTeams(false);
  }

  function cycleTheme() {
    const order: ThemePreference[] = ["system", "light", "dark"];
    const next = order[(order.indexOf(theme) + 1) % order.length]!;
    setTheme(next);
    applyThemePreference(next);
  }

  const navLinkClass =
    "rounded-lg px-3 py-1.5 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink [&.active]:bg-surface-3 [&.active]:text-ink";
  const menuItemClass =
    "block px-4 py-2 text-sm text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink";

  return (
    <header className="border-b border-line bg-surface px-6 py-2.5">
      <div className="flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2">
          <span className="text-lg font-semibold tracking-tight text-ink">
            MotionForge
          </span>
        </Link>

        {/* Navigation */}
        <nav className="flex items-center gap-1">
          <Link to="/create" className={navLinkClass}>
            Create
          </Link>
          <Link to="/templates" className={navLinkClass}>
            Templates
          </Link>
          <Link to="/renders" className={navLinkClass}>
            Renders
          </Link>
          {user?.role === "admin" && (
            <Link to="/admin" className={navLinkClass}>
              Admin
            </Link>
          )}
        </nav>

        {/* User Menu */}
        <div className="relative" ref={menuRef}>
          {isAuthenticated && user ? (
            <>
              <button
                onClick={() => {
                  setMenuOpen(!menuOpen);
                  setShowTeams(false);
                }}
                className="flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-surface-3"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-fg">
                  {user.username.slice(0, 1).toUpperCase()}
                </span>
                {user.username}
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-60 rounded-xl border border-line bg-surface py-1.5 shadow-lg">
                  {showTeams ? (
                    <>
                      {/* Team list */}
                      <div className="border-b border-line px-4 py-2">
                        <button
                          onClick={() => setShowTeams(false)}
                          className="text-xs text-ink-3 hover:text-ink"
                        >
                          &larr; Back
                        </button>
                      </div>
                      {teams?.map((team) => (
                        <button
                          key={team.id}
                          onClick={() => handleSwitchTeam(team.id)}
                          className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-surface-3 ${
                            team.id === activeTeamId
                              ? "font-medium text-accent"
                              : "text-ink-2"
                          }`}
                        >
                          {team.id === activeTeamId && (
                            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                          )}
                          <span className={team.id === activeTeamId ? "" : "ml-[14px]"}>
                            {team.name}
                          </span>
                          {team.is_personal && (
                            <span className="ml-auto text-[11px] text-ink-3">
                              personal
                            </span>
                          )}
                        </button>
                      ))}
                      <div className="border-t border-line">
                        <Link
                          to="/teams/new"
                          onClick={() => {
                            setMenuOpen(false);
                            setShowTeams(false);
                          }}
                          className="block px-4 py-2 text-sm text-accent hover:bg-surface-3"
                        >
                          + Create Team
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Normal menu */}
                      <button
                        onClick={() => setShowTeams(true)}
                        className="flex w-full items-center justify-between px-4 py-2 text-sm text-ink-2 hover:bg-surface-3 hover:text-ink"
                      >
                        <span>{teamName}</span>
                        <span className="text-xs text-ink-3">&rsaquo;</span>
                      </button>
                      <div className="border-t border-line" />
                      {activeTeam && !activeTeam.is_personal && (
                        <Link
                          to="/teams/$id/settings"
                          params={{ id: activeTeam.id }}
                          className={menuItemClass}
                          onClick={() => setMenuOpen(false)}
                        >
                          Team Settings
                        </Link>
                      )}
                      <Link
                        to="/settings"
                        className={menuItemClass}
                        onClick={() => setMenuOpen(false)}
                      >
                        Settings
                      </Link>
                      {user?.role === "admin" && (
                        <Link
                          to="/admin"
                          className={menuItemClass}
                          onClick={() => setMenuOpen(false)}
                        >
                          Admin
                        </Link>
                      )}
                      <button
                        onClick={cycleTheme}
                        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm text-ink-2 hover:bg-surface-3 hover:text-ink"
                      >
                        <span>Theme</span>
                        <span className="text-xs text-ink-3">
                          {THEME_LABELS[theme]}
                        </span>
                      </button>
                      <div className="border-t border-line" />
                      <button
                        onClick={handleLogout}
                        className="block w-full px-4 py-2 text-left text-sm text-ink-2 hover:bg-surface-3 hover:text-danger"
                      >
                        Logout
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <Link
              to="/login"
              className="rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
            >
              Login
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
